import { SafeError, SafePromise } from './safeAwait';
import { getOutboundRequestConcurrencyKey, OutboundRequestPolicy } from './outboundRequestPolicy';

const OUTBOUND_REQUEST_CONCURRENCY_KEY = '__BZ_OUTBOUND_REQUEST_CONCURRENCY__';
const DEFAULT_GLOBAL_OUTBOUND_REQUEST_CONCURRENCY = 56;
const DEFAULT_HOST_OUTBOUND_REQUEST_CONCURRENCY = 8;

type OutboundRequestQueueEntry = {
  host: string;
  policy?: OutboundRequestPolicy;
  resolve: () => void;
};

export type OutboundRequestSlotRelease = (() => void) & {
  activeGlobalAtAcquire: number;
  activeHostAtAcquire: number;
  host: string;
  queueDepthAtAcquire: number;
  queueWaitMs: number;
};

type OutboundRequestConcurrencyState = {
  activeByHost: Record<string, number>;
  activeGlobal: number;
  queue: OutboundRequestQueueEntry[];
};

const getGlobalWithOutboundRequestConcurrency = (): typeof globalThis & {
  [OUTBOUND_REQUEST_CONCURRENCY_KEY]?: OutboundRequestConcurrencyState;
} =>
  globalThis as typeof globalThis & {
    [OUTBOUND_REQUEST_CONCURRENCY_KEY]?: OutboundRequestConcurrencyState;
  };

const getConfiguredConcurrencyLimit = (value: string | undefined, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const getOutboundRequestConcurrencyState = (): OutboundRequestConcurrencyState => {
  const globalWithStats = getGlobalWithOutboundRequestConcurrency();
  let state = globalWithStats[OUTBOUND_REQUEST_CONCURRENCY_KEY];

  if (!state || typeof state !== 'object') {
    state = {
      activeByHost: {},
      activeGlobal: 0,
      queue: [],
    };
    globalWithStats[OUTBOUND_REQUEST_CONCURRENCY_KEY] = state;
    return state;
  }

  if (!state.activeByHost || typeof state.activeByHost !== 'object') {
    state.activeByHost = {};
  }
  if (!Array.isArray(state.queue)) {
    state.queue = [];
  }
  if (typeof state.activeGlobal !== 'number' || !Number.isFinite(state.activeGlobal)) {
    state.activeGlobal = 0;
  }

  return state;
};

const canAcquireOutboundRequestSlot = (
  state: OutboundRequestConcurrencyState,
  host: string,
  policy?: OutboundRequestPolicy,
): boolean => {
  const globalLimit = getConfiguredConcurrencyLimit(
    process.env.BZ_OUTBOUND_REQUEST_GLOBAL_CONCURRENCY ?? process.env.BZ_UPSTREAM_GLOBAL_CONCURRENCY,
    DEFAULT_GLOBAL_OUTBOUND_REQUEST_CONCURRENCY,
  );
  const hostLimit =
    policy?.hostConcurrency != null
      ? policy.hostConcurrency
      : getConfiguredConcurrencyLimit(
          process.env.BZ_OUTBOUND_REQUEST_HOST_CONCURRENCY ?? process.env.BZ_UPSTREAM_HOST_CONCURRENCY,
          DEFAULT_HOST_OUTBOUND_REQUEST_CONCURRENCY,
        );
  return state.activeGlobal < globalLimit && (state.activeByHost[host] ?? 0) < hostLimit;
};

const wakeFirstEligibleOutboundRequestWaiter = () => {
  const state = getOutboundRequestConcurrencyState();
  const nextIndex = state.queue.findIndex(entry => canAcquireOutboundRequestSlot(state, entry.host, entry.policy));
  if (nextIndex === -1) return;
  const [next] = state.queue.splice(nextIndex, 1);
  next.resolve();
};

export const acquireOutboundRequestSlot = async (
  url: string,
  policy?: OutboundRequestPolicy,
): SafePromise<OutboundRequestSlotRelease> => {
  if (typeof window !== 'undefined') {
    const release = (() => undefined) as OutboundRequestSlotRelease;
    release.activeGlobalAtAcquire = 0;
    release.activeHostAtAcquire = 0;
    release.host = '';
    release.queueDepthAtAcquire = 0;
    release.queueWaitMs = 0;
    return { ok: release };
  }

  const host = getOutboundRequestConcurrencyKey(url, policy);
  const state = getOutboundRequestConcurrencyState();
  const queuedAtMs = Date.now();

  while (!canAcquireOutboundRequestSlot(state, host, policy)) {
    let queueEntry: OutboundRequestQueueEntry;
    const queued = await new Promise<boolean>(resolve => {
      const timeout =
        policy?.queueTimeoutMs !== undefined
          ? setTimeout(() => {
              state.queue = state.queue.filter(entry => entry !== queueEntry);
              resolve(false);
            }, policy.queueTimeoutMs)
          : undefined;
      queueEntry = {
        host,
        policy,
        resolve: () => {
          if (timeout) clearTimeout(timeout);
          resolve(true);
        },
      };
      state.queue.push(queueEntry);
    });
    if (!queued) {
      return {
        err: new SafeError(
          'outbound request concurrency limit rejected request',
          'outbound_request_concurrency_rejected',
          {
            outboundRequestPolicy: policy?.name,
            status: 503,
          },
        ),
      };
    }
  }

  state.activeGlobal += 1;
  state.activeByHost[host] = (state.activeByHost[host] ?? 0) + 1;

  const release = (() => {
    state.activeGlobal = Math.max(state.activeGlobal - 1, 0);
    state.activeByHost[host] = Math.max((state.activeByHost[host] ?? 1) - 1, 0);
    wakeFirstEligibleOutboundRequestWaiter();
  }) as OutboundRequestSlotRelease;
  release.activeGlobalAtAcquire = state.activeGlobal;
  release.activeHostAtAcquire = state.activeByHost[host] ?? 0;
  release.host = host;
  release.queueDepthAtAcquire = state.queue.length;
  release.queueWaitMs = Date.now() - queuedAtMs;

  return {
    ok: release,
  };
};
